<template>
	<section ref="el" data-component="capabilities">
		<div class="g-row">
			<div class="g-col xxl-8 sm-14 xs-15 title-col">
				<h3 class="-title-4 title">
					<SanityContent :value="title" :components="components" />
				</h3>

				<div class="built-for">
					<MediaAsset :media="builtFor.image" />

					<div class="text-wrapper">
						<h6 class="-body-big-medium small-title">
							<SanityContent :value="builtFor.title" :components="components" />
						</h6>

						<p class="-body-regular">{{ builtFor.description }}</p>
					</div>
				</div>
			</div>
			<div class="g-col xxl-7 xxl-offset-1 sm-16 sm-offset-0 capabilities-col">
				<p class="-body-big-regular upper-title">{{ capabilitiesTitle }}</p>

				<div class="capabilities-wrapper">
					<div v-for="(capability, i) in capabilities" :key="i" class="capability">
						<div class="title-wrapper">
							<div class="icon-wrapper">
								<Icon :slug="capability.icon" class="icon" size="md" />
							</div>
							<h4 class="-headline-2 title">{{ capability.title }}</h4>
						</div>

						<p class="-body-regular description">{{ capability.description }}</p>
					</div>
				</div>
			</div>

			<span class="last-border" />
		</div>
	</section>
</template>

<script setup>
import { h } from 'vue'
import { MediaAsset, Icon } from '~/components/core'

defineProps({
	builtFor: { type: Object, default: undefined },
	capabilities: { type: Array, default: undefined },
	title: { type: Array, default: undefined },
	capabilitiesTitle: { type: String, default: undefined },
})

const components = {
	block: {
		normal: (_, { slots }) => h('span', { class: 'title-line' }, slots.default?.()),
	},
}

const el = ref(null)
const context = inject('pageContext')

const setup = () => {}
const init = () => {}
onMounted(() => {
	const loader = context?.$page?.loader

	loader.loaded.then(() => setup())
	loader.transitionReady.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='capabilities'] {
	position: relative;

	margin-top: rs(200, 100);

	.title-col {
		display: flex;
		flex-direction: column;
		justify-content: space-between;

		.title {
			white-space: pre-line;

			& :deep(.title-line) {
				display: block;
				@include inherit-font;

				color: var(--color-neutral-tints-900);

				&:first-child {
					text-indent: column-width(2, true);
				}
			}

			& :deep(strong) {
				color: var(--color-river-blue-500);
				@include inherit-font;
			}
		}

		.built-for {
			display: flex;
			flex-direction: row;
			align-items: flex-end;

			gap: var(--grid-gap);

			width: column-width(5);

			@include max-width($md) {
				width: column-width(6);
			}

			@include max-width($sm) {
				width: column-width(16);
				margin-top: rs(55, 55);
			}

			img {
				flex-shrink: 0;

				width: column-width(2);
				border-radius: rs(12, 12);
				overflow: hidden;

				@include max-width($sm) {
					width: column-width(7);
				}
			}

			.text-wrapper {
				@include max-width($sm) {
					margin-left: column-width(1, true);
				}
			}

			.small-title {
				margin-bottom: rs(12, 12);

				& :deep(.title-line) {
					display: block;
					@include inherit-font;

					color: var(--color-neutral-tints-900);
				}

				& :deep(strong) {
					color: var(--color-river-blue-500);
					@include inherit-font;
				}
			}
		}
	}

	.capabilities-col {
		margin-top: rs(48);

		.upper-title {
			color: var(--color-neutral-tints-400);
			margin-bottom: rs(16, 16);
		}

		.capabilities-wrapper {
			.capability {
				display: flex;
				flex-direction: row;
				gap: var(--grid-gap);

				padding: rs(36, 36) 0;

				border-bottom: 1px solid var(--color-river-blue-100);

				&:first-child {
					border-top: 1px solid var(--color-river-blue-100);
				}

				&:last-child {
					border-bottom: none;
					padding-bottom: 0;
				}

				.title-wrapper {
					display: flex;
					flex-direction: row;
					gap: rs(12, 12);
					width: column-width(4);

					@include max-width($sm) {
						width: column-width(8);
					}

					.icon-wrapper {
						flex-shrink: 0;

						--icon-color: #{var(--color-river-blue-500)};

						display: flex;
						justify-content: center;
						align-items: center;

						width: rs(40, 40);
						height: rs(40, 40);

						border-radius: 50px;
						background-color: var(--color-river-blue-100);
					}
				}
				.description {
					flex-grow: 0;
					width: column-width(3);
					color: var(--color-neutral-tints-700);

					@include max-width($sm) {
						width: column-width(8);
					}
				}
			}
		}
	}

	.last-border {
		width: 100%;
		height: 1px;
		background-color: var(--color-river-blue-100);
		margin-top: rs(32, 32);
	}
}
</style>
